import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Create the context
const AuthContext = createContext();

// Free defaults for entitlements when logged out or loading
const ENTITLEMENT_DEFAULTS = {
    isPremium: false,
    trialEligible: false,
    plan: 'free',
    premiumSource: 'none',
    premiumUntil: null,
    cancelAtPeriodEnd: false,
    optimizeUsedToday: 0,
    optimizeLimit: 5,
};

// Provider component that wraps your app
export const AuthProvider = ({ children }) => {

    // State to hold the user object (e.g., { email, role }) or null if not logged in
    const [user, setUser] = useState(null);
    // State to track if the initial authentication check is in progress
    const [loading, setLoading] = useState(true);

    // Entitlements state (additive — existing consumers don't break)
    const [entitlements, setEntitlements] = useState({ ...ENTITLEMENT_DEFAULTS, entitlementsLoading: false });

    // Fetch entitlements from the backend
    const fetchEntitlements = useCallback(async () => {
        setEntitlements(prev => ({ ...prev, entitlementsLoading: true }));
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/get_entitlements.php`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Accept': 'application/json' },
            });

            if (!response.ok) {
                setEntitlements({ ...ENTITLEMENT_DEFAULTS, entitlementsLoading: false });
                return;
            }

            const result = await response.json();

            if (result.status === 'success') {
                setEntitlements({
                    isPremium: !!result.is_premium,
                    trialEligible: !!result.trial_eligible,
                    plan: result.plan || 'free',
                    premiumSource: result.premium_source || 'none',
                    premiumUntil: result.premium_until || null,
                    cancelAtPeriodEnd: !!result.cancel_at_period_end,
                    optimizeUsedToday: result.optimize_used_today ?? 0,
                    optimizeLimit: result.optimize_limit ?? 5,
                    entitlementsLoading: false,
                });
            } else {
                setEntitlements({ ...ENTITLEMENT_DEFAULTS, entitlementsLoading: false });
            }
        } catch (error) {
            console.error('AuthContext entitlements fetch error:', error);
            setEntitlements({ ...ENTITLEMENT_DEFAULTS, entitlementsLoading: false });
        }
    }, []);

    // Function to check the backend session status when the app loads or refreshes
    const checkAuthStatus = useCallback(async () => {
        setLoading(true); // Start loading indicator
        try {
            // Fetch request to your PHP script that checks $_SESSION
            const response = await fetch('https://renaisons.com/api/check_auth.php', { // Adjust URL if needed
                method: 'GET', // Or POST, ensure it matches your check_auth.php
                credentials: 'include', // *** ESSENTIAL for sending session cookie ***
                headers: {
                    'Accept': 'application/json',
                },
            });

            if (!response.ok) {
                // If response status is not 2xx (e.g., 401 Unauthorized), assume not logged in
                setUser(null);
                setEntitlements({ ...ENTITLEMENT_DEFAULTS, entitlementsLoading: false });
                return; // Stop processing further for this case
            }

            const result = await response.json();

            if (result.status === 'success' && result.user) {
                setUser(result.user); // Set user data if session is valid
                // Load entitlements now that we know the user is authenticated
                await fetchEntitlements();
            } else {
                setUser(null); // Clear user if session is invalid or data is missing
                setEntitlements({ ...ENTITLEMENT_DEFAULTS, entitlementsLoading: false });
            }
        } catch (error) {
            console.error('Auth check fetch error:', error);
            setUser(null); // Assume not logged in on network errors, etc.
            setEntitlements({ ...ENTITLEMENT_DEFAULTS, entitlementsLoading: false });
        } finally {
            setLoading(false); // Stop loading indicator regardless of outcome
        }
    }, [fetchEntitlements]); // useCallback with empty dependency array ensures this function is stable

    // Run the authentication check once when the AuthProvider mounts
    useEffect(() => {
        checkAuthStatus();
    }, [checkAuthStatus]); // Run checkAuthStatus whenever it changes (only on mount due to useCallback)

    // Function called by LoginModal/LoginPage upon successful login from API
    const login = (userData) => {
        if (userData && userData.email && userData.role) {
            setUser(userData); // Update the global user state
            // Re-fetch entitlements on login
            fetchEntitlements();
        } else {
            console.error("Login function received invalid user data:", userData);
            setUser(null); // Ensure state is null if data is bad
        }
    };

    // Function to log the user out
    const logout = async () => {
        setUser(null); // Immediately update UI state to logged out
        setEntitlements({ ...ENTITLEMENT_DEFAULTS, entitlementsLoading: false });
        try {
            // Call the PHP script to destroy the server-side session
            await fetch('https://renaisons.com/api/logout.php', { // Adjust URL if needed
                method: 'POST', // Or GET, ensure it matches your logout.php
                credentials: 'include', // *** ESSENTIAL for sending session cookie ***
            });
            // No need to check response unless you want to confirm server action
        } catch (error) {
            console.error('Logout fetch error:', error);
            // Even if server call fails, keep user logged out in frontend state
        }
        // Optionally add navigation after logout, e.g., using useNavigate() in the component calling logout
    };

    // Provide the user state, loading state, auth functions, and entitlements to children
    // Existing consumers (user, login, logout, loading, checkAuthStatus) are untouched — additive only
    return (
        <AuthContext.Provider value={{
            user,
            login,
            logout,
            loading,
            checkAuthStatus,
            // Entitlements (new — additive)
            isPremium: entitlements.isPremium,
            trialEligible: entitlements.trialEligible,
            plan: entitlements.plan,
            premiumSource: entitlements.premiumSource,
            premiumUntil: entitlements.premiumUntil,
            cancelAtPeriodEnd: entitlements.cancelAtPeriodEnd,
            optimizeUsedToday: entitlements.optimizeUsedToday,
            optimizeLimit: entitlements.optimizeLimit,
            entitlementsLoading: entitlements.entitlementsLoading,
            refreshEntitlements: fetchEntitlements,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to easily consume the AuthContext in components
export const useAuth = () => useContext(AuthContext);
